import { useSearchParams } from "react-router";
import styled from "styled-components";
import { Trans } from "@lingui/react/macro";
import {
  type CalendarDay,
  type CalendarDayUser,
  type CalendarTimeOff,
  type RedLineData,
  useCalendarStore,
} from "../../store/useCalendarStore";
import { ResourceColumn } from "./ResourceColumn/ResourceColumn";
import UserColumn from "./UserColumn";
import { useShallow } from "zustand/react/shallow";

const DayColumn = ({
  day,
  timeoffs,
  isOnlyDay,
  redLineData,
  shouldHighlight,
}: {
  day: CalendarDay;
  timeoffs: CalendarTimeOff[];
  isOnlyDay: boolean;
  redLineData: RedLineData | null;
  shouldHighlight: boolean;
}) => {
  const { calendarMode, resources, calendarDays, showOnlyUsersWithShifts } =
    useCalendarStore(
      useShallow((state) => ({
        calendarMode: state.calendarMode,
        resources: state.resources,
        calendarDays: state.calendarData.days || [],
        showOnlyUsersWithShifts: state.filterItems.showOnlyUsersWithShifts,
      })),
    );
  const [params] = useSearchParams();

  const resourcesInOrder = resources.filter((r) => r.isEnabledInFilter);
  const userColumnWidthPx = 80;
  const fixedDayWidthPx =
    calendarMode === "users"
      ? (params.get("u")
          ? 1
          : Math.max(
              1,
              ...calendarDays.map((calendarDay) => calendarDay.users.length),
            )) * userColumnWidthPx
      : undefined;
  const usersToRender = day.users.filter(
    (u: CalendarDayUser) =>
      !params.get("u") || u.userId === Number(params.get("u")),
  );
  const showNoUsersDueToShiftFilter =
    calendarMode === "users" &&
    !params.get("u") &&
    day.users.length === 0 &&
    showOnlyUsersWithShifts;

  return (
    <Wrapper
      className="dayColumn"
      data-date={day.date}
      isOnlyDay={isOnlyDay}
      shouldHighlight={shouldHighlight}
      fixedDayWidthPx={fixedDayWidthPx}
    >
      {redLineData && (
        <div
          className="border-b-0.5 pointer-events-none absolute left-0 z-9999 w-full border-1 border-red-500"
          style={{ top: redLineData.yOffset }}
        >
          <div className="absolute -left-px z-9999 h-[20px] w-[45px] -translate-x-full -translate-y-1/2 rounded-[5px] bg-red-500 text-center text-[12px] leading-[20px] font-bold text-white">
            {redLineData.currentTime}
          </div>
        </div>
      )}

      {calendarMode === "users" && (
        <div className={"users"}>
          {usersToRender.map((user: CalendarDayUser, index: number) => (
            <UserColumn
              isLastChild={index === usersToRender.length - 1}
              user={user}
              key={index}
              date={day.date}
              timeoffs={timeoffs.filter(
                (t: CalendarTimeOff) => t.userId === user.userId,
              )}
              usersInOrder={usersToRender.map((u: CalendarDayUser) => u.userId)}
            />
          ))}
        </div>
      )}

      {showNoUsersDueToShiftFilter && (
        <div className="users">
          <div className="flex items-center justify-center text-center text-gray-500">
            <Trans>Ni zaposlenih, ki ustrezajo filtru.</Trans>
          </div>
        </div>
      )}

      {calendarMode === "resources" && (
        <div className="resources">
          {resourcesInOrder.flatMap((resource) =>
            !params.get("r") || resource.id === Number(params.get("r"))
              ? [
                  <ResourceColumn
                    resource={resource}
                    date={day.date}
                    timeoffs={timeoffs.filter(
                      (t: CalendarTimeOff) => t.resourceId === resource.id,
                    )}
                    resourcesInOrder={resourcesInOrder.map((r) => r.id)}
                    key={resource.id}
                  />,
                ]
              : [],
          )}
        </div>
      )}

      {day.disableOnHolidays && <HolidayDisabledOverlay />}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  isOnlyDay: boolean;
  shouldHighlight: boolean;
  fixedDayWidthPx: number | undefined;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: auto;
  flex: 1;
  transition: background-color 0.3s ease-out;

  ${(props) =>
    !props.isOnlyDay &&
    `
      border-right: 2px solid #c3c5c6;
      :not(:last-child) {
        border-right: 3px solid #6b7280;
      }
      @media (max-width: 768px) {
        width: ${
          props.fixedDayWidthPx != null
            ? `${props.fixedDayWidthPx}px`
            : "fit-content"
        };
        min-width: ${
          props.fixedDayWidthPx != null
            ? `${props.fixedDayWidthPx}px`
            : "fit-content"
        };
        flex: ${props.fixedDayWidthPx != null ? "0 0 auto" : "1"};
      }
    `}

  ${(props) =>
    props.shouldHighlight &&
    `
      animation: highlightPulse 2s ease-out;
    `}

  @keyframes highlightPulse {
    0% {
      background-color: rgba(224, 236, 244, 1);
    }
    50% {
      background-color: rgba(224, 236, 244, 0.7);
    }
    100% {
      background-color: transparent;
    }
  }

  .users,
  .resources {
    display: flex;
  }
`;

const HolidayDisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(
    159.5deg,
    transparent,
    transparent 8px,
    rgb(243, 244, 247) 8px,
    rgb(243, 244, 247) 18px
  );
  background-color: white;
  pointer-events: none;
  z-index: -2;
`;

export default DayColumn;
